import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Container,
  useMediaQuery,
  IconButton,
  Typography,
  Divider,
  Link,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import MateriomLogo from "../ui/MateriomLogo";
import { UserDropdownMenu } from "../TopBar";
import { useTheme } from "@mui/material/styles";
import { createTopBarStyles } from "./styles";
import { ROUTES } from "../../constants/routes";
import { useAuth } from "../../hooks/useAuth";
import { User } from "../../types/api";
import { FOOTER_URLS } from "../../constants";
import { FeatureRequestBanner } from "../FeatureRequestBanner";
import { useAuthContext } from "../../contexts/AuthContext";

interface AuthenticatedProps {
  children: React.ReactNode;
}

/**
 * Authenticated Layout Component
 *
 * A responsive navigation bar with MateriomLogo and navigation buttons.
 * Shows full navigation on desktop and full-screen mobile menu on mobile.
 * Navigation paths are managed centrally in constants/routes.ts.
 * Includes a footer with Materiom copyright notice.
 */
const Authenticated: React.FC<AuthenticatedProps> = ({ children }) => {
  // Hooks
  const navigate = useNavigate();
  const location = useLocation();
  const [showingNavigationDropdown, setShowingNavigationDropdown] =
    useState(false);

  // Authentication hooks
  const { user } = useAuthContext();
  const { logout } = useAuth();

  // Styles
  const theme = useTheme();
  const topBarStyles = createTopBarStyles(theme);

  // Use Material-UI breakpoints to determine if we're on mobile
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const isActive = (path: string) => location.pathname === path;

  // Navigation items using centralized route constants
  const navItems = [
    { label: "Dashboard", path: ROUTES.DASHBOARD },
    { label: "Materiom AI", path: ROUTES.MATERIOM_AI },
    { label: "Materials Database", path: ROUTES.MATERIALS_DATABASE },
    { label: "User Directory", path: ROUTES.USER_DIRECTORY },
    { label: "Contribution Portal", path: ROUTES.CONTRIBUTION_PORTAL },
  ];

  const handleMobileMenuToggle = () => {
    setShowingNavigationDropdown(!showingNavigationDropdown);
  };

  const handleMobileNavigation = (path: string) => {
    navigate(path);
    setShowingNavigationDropdown(false);
  };

  const handleLogout = () => {
    logout();
    setShowingNavigationDropdown(false);
  };

  // Get user name
  const getUserName = (user: User | null) => {
    if (user) {
      return user.name || "";
    }
    return "";
  };

  return (
    <Box
      sx={{
        ...topBarStyles.container,
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <AppBar position="fixed" elevation={1} sx={topBarStyles.appBar}>
        <Container maxWidth="xl">
          <Toolbar sx={topBarStyles.toolbar}>
            {/* Logo */}
            <Box sx={topBarStyles.logoContainer}>
              <MateriomLogo
                sx={topBarStyles.logo}
                onClick={() => navigate(ROUTES.DASHBOARD)}
              />
            </Box>

            {/* Desktop Navigation - Hidden on mobile */}
            {!isMobile && (
              <Box sx={topBarStyles.desktopNavigation}>
                {navItems.map((item) => (
                  <Button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    sx={topBarStyles.navButton(isActive(item.path))}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            )}

            {/* Mobile/Desktop Navigation */}
            {isMobile ? (
              <IconButton
                color="inherit"
                aria-label="toggle mobile menu"
                edge="end"
                onClick={handleMobileMenuToggle}
                disableRipple
                sx={topBarStyles.mobileMenuButton}
              >
                {showingNavigationDropdown ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            ) : (
              <UserDropdownMenu handleLogout={handleLogout} user={user} />
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Full-Screen Menu */}
      {isMobile && showingNavigationDropdown && (
        <Box sx={topBarStyles.mobileMenuOverlay}>
          {/* Main Navigation Links */}
          <Box sx={topBarStyles.mobileNavSection}>
            {navItems.map((item) => (
              <Box
                key={item.path}
                onClick={() => handleMobileNavigation(item.path)}
                sx={topBarStyles.mobileNavItem(isActive(item.path))}
              >
                <Typography
                  sx={topBarStyles.mobileNavItemText(isActive(item.path))}
                >
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>

          <Divider />

          {/* User Information Section */}
          <Box sx={topBarStyles.mobileUserSection}>
            <Box sx={topBarStyles.mobileUserInfo}>
              <Typography sx={topBarStyles.mobileUserName}>
                {getUserName(user)}
              </Typography>
            </Box>

            <Divider sx={{ mt: 2 }} />

            {/* Update Details Button */}
            <Box
              onClick={() => handleMobileNavigation(ROUTES.UPDATE_DETAILS)}
              sx={topBarStyles.mobileNavItem(isActive(ROUTES.UPDATE_DETAILS))}
            >
              <Typography
                sx={topBarStyles.mobileNavItemText(
                  isActive(ROUTES.UPDATE_DETAILS)
                )}
              >
                Update Details
              </Typography>
            </Box>

            {/* Logout Button */}
            <Box onClick={handleLogout} sx={topBarStyles.mobileLogoutButton}>
              <Typography
                sx={topBarStyles.mobileLogoutText}
                onClick={handleLogout}
              >
                Log Out
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Content */}
      <Box sx={{ ...topBarStyles.content, flex: 1 }}>{children}</Box>

      {/* Feature Request Banner */}
      <FeatureRequestBanner user={user} />

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 5,
          backgroundColor: "white",
          mt: 6,
          borderTop: "2px solid",
          borderColor: "divider",
        }}
      >
        <Container maxWidth="xl">
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ fontSize: "0.875rem" }}
          >
            © {new Date().getFullYear()} Materiom |{" "}
            <Link
              href={FOOTER_URLS.TERMS_AND_CONDITIONS}
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
              underline="hover"
            >
              Terms & Conditions
            </Link>{" "}
            |{" "}
            <Link
              href={FOOTER_URLS.PRIVACY_POLICY}
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
              underline="hover"
            >
              Privacy Policy
            </Link>
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Authenticated;
